import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Check, Download, ArrowRight, CalendarCheck, ArrowLeft } from "lucide-react";
import { SEO } from "@/components/site/SEO";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { absoluteBackendUrl, fetchSaveSpendVideo } from "@/lib/forms";
import { FREEBIES, BOOKING_URL, readFreebieAccess } from "@/lib/freebies";

const BookACall = () => (
  <div className="mt-10 rounded-card bg-legacy text-ivory p-7 md:p-9">
    <p className="eyebrow text-gold">Next step</p>
    <h2 className="mt-3 font-serif text-2xl md:text-3xl leading-tight">Want help applying this to your situation?</h2>
    <p className="mt-3 text-ivory/80 text-[0.95rem] leading-relaxed">
      Book a free, no-pressure strategy call with Shawn to talk through your goals and see how these
      ideas fit your full financial picture.
    </p>
    <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" data-testid="thankyou-book-call"
      className="btn-base btn-gold mt-6 text-[0.8rem] tracking-[0.12em] uppercase">
      <CalendarCheck size={16} />Book a Free Strategy Call
    </a>
  </div>
);

const VideoEmbed = ({ accessUrl }) => {
  const [videoId, setVideoId] = useState(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    if (!accessUrl) { setError(true); return; }
    fetchSaveSpendVideo(accessUrl)
      .then((d) => { if (d.status === "ok") setVideoId(d.video_id); else setError(true); })
      .catch(() => setError(true));
  }, [accessUrl]);
  return (
    <div className="mt-6 aspect-video w-full rounded-card overflow-hidden bg-black border border-taupe/40">
      {videoId ? (
        <iframe className="h-full w-full" src={`https://www.youtube-nocookie.com/embed/${videoId}?rel=0`}
          title="Save & Spend System Overview" loading="lazy" allowFullScreen
          allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          data-testid="thankyou-video" />
      ) : (
        <div className="h-full w-full flex items-center justify-center text-taupe text-sm px-6 text-center">
          {error ? "This overview is temporarily unavailable. Please request it again." : "Loading your overview\u2026"}
        </div>
      )}
    </div>
  );
};

export default function ThankYou({ freebieKey }) {
  const freebie = FREEBIES[freebieKey];
  const navigate = useNavigate();
  const [access, setAccess] = useState(undefined); // undefined=loading, null=missing

  useEffect(() => {
    setAccess(readFreebieAccess(freebieKey) || null);
  }, [freebieKey]);

  const hasAccess = access && (access.download_url || access.access_url);

  return (
    <div className="min-h-screen bg-ivory" data-testid={`thankyou-page-${freebieKey}`}>
      <SEO title={`${freebie.resourceName} \u2014 Thank You | Legacy Life & Retirement`} noindex />
      <Header />
      <main className="relative bg-ivory pt-[120px] md:pt-[150px] pb-24 md:pb-28 overflow-hidden">
        <div className="pointer-events-none absolute inset-0 grid-lines opacity-40" />
        <div className="container-lr relative max-w-3xl">
          {access === undefined ? null : !hasAccess ? (
            <div data-testid="thankyou-no-access" className="rounded-card bg-white border border-taupe/40 p-8 md:p-10 text-center">
              <h1 className="font-serif text-2xl md:text-3xl text-legacy leading-tight">Let's get you your resource</h1>
              <p className="mt-4 text-slate leading-relaxed">
                It looks like you reached this page directly. Please request
                {" "}<span className="text-legacy font-medium">{freebie.resourceName}</span> on the Resources page first —
                we'll send you straight back here with your secure access.
              </p>
              <button type="button" onClick={() => navigate(`/resources#${freebie.anchor}`)}
                data-testid="thankyou-back-to-form" className="btn-base btn-primary mt-7 text-[0.8rem] tracking-[0.12em] uppercase">
                <ArrowLeft size={16} />Go to the form
              </button>
            </div>
          ) : (
            <>
              <span className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-gold text-legacy"><Check size={26} /></span>
              <p className="eyebrow mt-6">Request received</p>
              <h1 className="mt-3 font-serif font-semibold tracking-display text-legacy text-3xl md:text-4xl leading-[1.08]">
                {freebie.title}
              </h1>
              <p className="mt-5 text-slate text-base md:text-lg leading-relaxed">{freebie.blurb}</p>

              {freebie.kind === "video" ? (
                <VideoEmbed accessUrl={access.access_url} />
              ) : (
                <div className="mt-8">
                  <a href={absoluteBackendUrl(access.download_url)} target="_blank" rel="noopener noreferrer"
                    data-testid="thankyou-download" className="btn-base btn-gold text-[0.82rem] tracking-[0.1em] uppercase">
                    <Download size={17} />Download {freebie.resourceName}
                  </a>
                  <p className="mt-3 text-[0.72rem] text-slate/70">
                    This is a secure, time-limited link and will expire shortly for your protection.
                  </p>
                </div>
              )}

              <BookACall />

              <div className="mt-8">
                <Link to="/resources" className="inline-flex items-center gap-2 text-sm text-bronze hover:text-gold">
                  Browse more resources<ArrowRight size={15} />
                </Link>
              </div>
            </>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
